import { ActionIcon, Badge, Button, Group, Tooltip, VisuallyHidden } from '@mantine/core';
import { useDisclosure, useId } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import VersionHistoryModal from './VersionHistoryModal/VersionHistoryModal.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { Automation, getAutomationVersionLabel } from '@/types/automations.ts';
import SimplePublishReviewModal from '@/pages/Editor/components/Publication/SimplePublishReviewModal.tsx';
import { useEditorPublicationSummary } from '@/hooks/api/automations/useAutomationPublications.ts';
import classes from './VersionButton.module.css';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlineClock } from 'react-icons/hi';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';

export default function VersionButton({ automation }: { automation: Automation }) {
  const navigate = useNavigate();
  const { data: latest } = useRetrieveLatestAutomationVersion();
  const [historyOpened, history] = useDisclosure(false);
  const [publicationOpened, publication] = useDisclosure(false);
  const publicationSummary = useEditorPublicationSummary(automation.id);
  const { hasPendingEdits, staleDraft } = useEditVersionContext();
  const publicationReadinessMessageId = useId();
  if (!latest) return null;
  const publicationIsCurrent = publicationSummary.data?.journey === 'already_current';
  const publishLabel = `Publish ${getAutomationVersionLabel(latest)}`;
  const draftLabel = `Draft ${getAutomationVersionLabel(latest)}`;
  const publicationReadinessMessage = publicationSummary.isLoading
    ? 'Checking publish readiness…'
    : publicationSummary.isError
      ? 'Couldn’t check publish readiness. Refresh the editor and try again.'
      : undefined;
  return (
    <>
      <Group gap="sm">
        <Badge
          aria-label={`Current automation version: ${draftLabel}`}
          color="gray"
          variant="light"
          size="sm"
        >
          {draftLabel}
        </Badge>
        <Tooltip label="View Version History" withArrow position="left">
          <ActionIcon
            aria-label="View version history"
            color="#757575"
            onClick={history.open}
            variant="subtle"
          >
            <AppIcon Icon={HiOutlineClock} iconProps={{ strokeWidth: 1.5 }} />
          </ActionIcon>
        </Tooltip>
        <Tooltip
          label={publicationReadinessMessage}
          disabled={!publicationReadinessMessage}
          withArrow
        >
          <span
            style={{ display: 'inline-flex' }}
            tabIndex={publicationReadinessMessage ? 0 : undefined}
            aria-describedby={
              publicationReadinessMessage ? publicationReadinessMessageId : undefined
            }
          >
            <Button
              size="xs"
              classNames={{ root: classes.root }}
              loading={publicationSummary.isLoading}
              aria-describedby={
                publicationReadinessMessage ? publicationReadinessMessageId : undefined
              }
              disabled={
                hasPendingEdits ||
                Boolean(staleDraft) ||
                Boolean(automation.retired_at) ||
                publicationSummary.isLoading ||
                !publicationSummary.data ||
                publicationIsCurrent
              }
              onClick={publication.open}
            >
              {publishLabel}
            </Button>
          </span>
        </Tooltip>
        {publicationReadinessMessage && (
          <VisuallyHidden id={publicationReadinessMessageId}>
            {publicationReadinessMessage}
          </VisuallyHidden>
        )}
      </Group>
      <VersionHistoryModal
        opened={historyOpened}
        onClose={history.close}
        automation={automation}
        latestVersion={latest}
      />
      {publicationSummary.data && (
        <SimplePublishReviewModal
          opened={publicationOpened}
          onClose={publication.close}
          summary={publicationSummary.data}
          onOpenGuided={() => {
            publication.close();
            navigate(
              `/automations/${automation.id}/release?version=${encodeURIComponent(publicationSummary.data.draft_version?.id ?? latest.id)}&flow=guided`,
            );
          }}
          onOpenAdvanced={() => {
            publication.close();
            navigate(
              `/automations/${automation.id}/release?version=${encodeURIComponent(publicationSummary.data.draft_version?.id ?? latest.id)}`,
            );
          }}
          latestVersion={latest}
          publishedVersion={automation.published_version}
        />
      )}
    </>
  );
}
